import * as React from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import { useAlert } from "../contexts/AlertProvider";
import { pick } from "lodash";
import { API_BASE_URL } from "../config";
import { consumeReturnTo } from "../utils/session";
import { clearDrafts } from "../utils/userStorage";

function useLogin() {
  const [showPassword, setShowPassword] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { alert } = useAlert();
  const userProps = useUser();

  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const toggleShowPassword = () => {
    setShowPassword((show) => (show ? false : true));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCreateNewAccountClick = () => {
    const path = "createNewAccount";
    navigate(path);
  };

  const loginUser = async (loginUser) => {

    if (loginUser.email.length === 0 || loginUser.password.length === 0)
    {
      alert({
        title: "Failed to Login",
        detail: "Either email or password is empty",
        severity: "warning",
        duration: 5000,
      });
      return;
    }

    const options = {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...loginUser}),
    };

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/agents/login/v1`,
        options
      );

      const result = await response.json();

      if (result.status !== "SUCCESS") {
        alert({
          title: "Failed to Login",
          detail: result.failureReason,
          severity: "warning",
          duration: 5000,
        });
        return;
      }

      const user = pick(result.data, [
        "id",
        "roles",
        "firstName",
        "lastName",
        "lastLoginDate",
        "token",
      ]);

      alert({
        title: "Login Successful",
        detail: `Welcome back, ${user.firstName}`,
        severity: "success",
        duration: 5000,
      });

      userProps.setUser(user);
      // Only the agent now using this browser keeps unsaved drafts.
      clearDrafts({ exceptAgentId: user.id });
      const returnTo = consumeReturnTo(user.id);
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      navigate({
        pathname: "clientDashboard",
        search: createSearchParams({
            id: user.id
        }).toString()
    });
      
    } catch (error) {
      alert({
        title: "Error Occurred",
        detail: "Unable to login user at the moment",
        severity: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginHandlers = React.useMemo(
    () => ({
      toggle: toggleShowPassword,
      mouseDown: handleMouseDownPassword,
      newAccount: handleCreateNewAccountClick,
      login: loginUser
    }),
    []
  );

  const dialogHandlers = React.useMemo(
    () => ({
      open: handleClickOpen,
      close: handleClose,
    }),
    []
  );

  const loginProps = {
    showPassword,
    isLoading,
    loginHandlers,
  };

  const dialogProps = {
    open,
    dialogHandlers,
  };

  return {
    loginProps,
    dialogProps,
  };
}

export default useLogin;
